import { OrganizationIntegrationAccountItem } from "@common/schemas/organization-connection-groups.schema";

export interface ReconnectReminderPreview {
    /**
     * Best-effort. The server sends to the resolved user's email, which is not
     * necessarily the nullable `connectedBy.email` shown here.
     */
    recipient: string;
    subject: string;
    greeting: string;
    body: string;
}

/** Must match `REMINDER_SUBJECT` in organization-connection-reminder.service.ts. */
const SUBJECT = "Please reconnect your account in Kick";

/** The template's own fallback when a connection has no institution name. */
const BANK_FALLBACK = "bank";

/**
 * The EJS greeting is `Hello <%= user.firstName %>,` and the row only carries a
 * full name, so this takes the first token. A single-word name is its own first
 * name; an empty one degrades to a nameless greeting rather than "Hello ,".
 */
const greetingFor = (name: string | null): string => {
    const firstName = name?.trim().split(/\s+/)[0];
    return firstName ? `Hello ${firstName},` : "Hello,";
};

interface ReconnectReminderPreviewParams {
    item: OrganizationIntegrationAccountItem;
    /** The firm member sending it — `requesterName` in the template. */
    requesterName: string;
}

/**
 * Renders what the client will receive, so a firm member can approve it before
 * it is sent.
 *
 * Deliberately a copy of `templates/email/integrationReconnectReminder.ejs`
 * rather than a fetch: there is no preview endpoint. It will therefore drift if
 * that template changes, which is what the accompanying test pins. The
 * `base/header` and `base/footer` partials are not reproduced — the preview shows
 * the message, not the branded chrome around it.
 */
export const reconnectReminderPreview = ({
    item,
    requesterName,
}: ReconnectReminderPreviewParams): ReconnectReminderPreview => {
    const { connectedBy, login } = item.connection;
    const bankName = login.institutionName ?? BANK_FALLBACK;

    return {
        recipient: connectedBy?.email ?? connectedBy?.name ?? "the client",
        subject: SUBJECT,
        greeting: greetingFor(connectedBy?.name ?? null),
        body: `${requesterName} asked you to reconnect the ${bankName} account you connected to Kick. Visit Integrations to reconnect it.`,
    };
};
